/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */
import React from "react";
import { useTheme } from "../../theme/ThemeContext";

/**
 * Shared card shell for the constraint/index builders.
 * Props:
 *   mode         — "summary" | "edit"
 *   title        — header label
 *   summary      — node shown in summary mode
 *   children     — editor body shown in edit mode
 *   canEdit      — show Edit button in summary (default true)
 *   onEdit, onCancel, onSave, onRemove — callbacks
 *   saveDisabled — disables Save (default false)
 *   saveHint     — tooltip shown when saveDisabled
 */
export default function ConstraintCard({
  mode, title, summary, children,
  canEdit = true, onEdit, onCancel, onSave, onRemove,
  saveDisabled = false, saveHint = "",
}) {
  const { theme } = useTheme();
  const editing = mode === "edit";
  const cardStyle = {
    border: editing ? `1.5px dashed ${theme.primary}` : `1px solid ${theme.borderColor || "#dee2e6"}`,
    borderRadius: 8, padding: 12, marginBottom: 10, background: "#fafbff",
  };
  const iconBtn = (color) => ({ background: "none", border: "none", cursor: "pointer", color, fontSize: 18 });

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: editing ? 8 : 0 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: theme.primary }}>{title}</span>
        {!editing && (
          <span style={{ display: "flex", gap: 6 }}>
            {canEdit && (
              <button type="button" onClick={onEdit} style={iconBtn(theme.primary)} title="Edit">
                <i className="bi bi-pencil" />
              </button>
            )}
            <button type="button" onClick={onRemove} style={iconBtn("#dc3545")} title="Remove">
              <i className="bi bi-trash" />
            </button>
          </span>
        )}
      </div>

      {editing ? (
        <>
          {children}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" onClick={onCancel}
              style={{ fontSize: 12, color: "#555", background: "none", border: "1px solid #bbb", borderRadius: 6, padding: "3px 14px", cursor: "pointer" }}>
              Cancel
            </button>
            <button type="button" onClick={onSave} disabled={saveDisabled} title={saveDisabled ? saveHint : ""}
              style={{ fontSize: 12, background: saveDisabled ? "#9ca3af" : theme.primary, color: "#fff", border: "none", borderRadius: 6, padding: "3px 16px", cursor: saveDisabled ? "not-allowed" : "pointer" }}>
              ✓ Save
            </button>
          </div>
        </>
      ) : (
        <div style={{ marginTop: 4 }}>{summary}</div>
      )}
    </div>
  );
}
